import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Link } from "react-router-dom";
import { LogOut, Loader2, Mail, Phone, ArrowLeft, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sv_staff_token";

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Accesso effettuato.");
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-ink/25 py-3 text-lg font-light focus:outline-none focus:border-ink transition-colors duration-300";

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="back-to-site" className="inline-flex items-center gap-2 text-sm text-muted hover:text-ink link-underline mb-12">
          <ArrowLeft className="w-4 h-4" /> Torna al sito
        </Link>
        <p className="text-[11px] uppercase tracking-[0.25em] text-muted">Area riservata</p>
        <h1 className="mt-3 font-serif text-5xl font-light tracking-tight">Studio Valle</h1>
        <p className="mt-3 text-muted">Accedi per gestire le richieste di prenotazione.</p>
        <form onSubmit={submit} data-testid="login-form" className="mt-10 space-y-8">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted">Email</label>
            <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={field} placeholder="staff@studiovalle.it" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted">Password</label>
            <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={field} placeholder="••••••••" />
          </div>
          <button data-testid="login-submit-button" type="submit" disabled={loading} className="group relative inline-flex items-center gap-3 overflow-hidden border border-ink px-9 py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-60">
            <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            <span className="relative flex items-center gap-3 group-hover:text-canvas transition-colors duration-500">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}Accedi
            </span>
          </button>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function Dashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [items, setItems] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setItems(null);
  }, []);

  const load = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/consultations`, { headers: { Authorization: `Bearer ${t}` } });
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Sessione scaduta. Accedi di nuovo."); logout(); }
      else toast.error("Errore nel caricamento.");
    }
  }, [logout]);

  useEffect(() => { if (token) load(token); }, [token, load]);

  if (!token) return (<><Toaster position="bottom-right" theme="dark" /><LoginScreen onLogin={setToken} /></>);

  return (
    <div className="min-h-screen bg-canvas">
      <Toaster position="bottom-right" theme="dark" />
      <header className="border-b border-ink/10 bg-canvas/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-2xl">Studio Valle</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-muted">Richieste</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="text-sm text-muted hover:text-ink link-underline hidden sm:inline">Vai al sito</Link>
            <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm border border-ink/20 px-4 py-2 hover:border-ink transition-colors duration-300">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-14">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-muted">Prenotazioni</p>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tight">Richieste di valutazione</h1>
          </div>
          {items && <span data-testid="requests-count" className="font-serif text-5xl text-ink/20">{String(items.length).padStart(2, "0")}</span>}
        </div>

        {items === null ? (
          <div className="py-32 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted" /></div>
        ) : items.length === 0 ? (
          <div data-testid="empty-state" className="py-32 text-center text-muted">
            <Inbox className="w-10 h-10 mx-auto mb-4 opacity-40" />
            <p>Ancora nessuna richiesta.</p>
          </div>
        ) : (
          <div data-testid="requests-list" className="border-t border-ink/10">
            {items.map((c) => (
              <div key={c.id} data-testid="request-row" className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 py-7 border-b border-ink/10 items-start hover:bg-sand/50 transition-colors duration-300 md:px-4 md:-mx-4">
                <div className="md:col-span-3">
                  <p className="font-serif text-2xl leading-none">{c.nome}</p>
                  <p className="mt-2 text-xs text-muted">{formatDate(c.created_at)}</p>
                </div>
                <div className="md:col-span-3 space-y-1.5 text-sm text-ink/80">
                  <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-ink link-underline w-fit"><Mail className="w-3.5 h-3.5 text-sage" />{c.email}</a>
                  <a href={`tel:${c.telefono}`} className="flex items-center gap-2 hover:text-ink link-underline w-fit"><Phone className="w-3.5 h-3.5 text-sage" />{c.telefono}</a>
                </div>
                <div className="md:col-span-2">
                  <span className="inline-block border border-ink/15 px-3 py-1.5 text-[12px] text-ink/70 leading-snug">{c.interesse}</span>
                </div>
                <p className="md:col-span-4 text-sm text-muted leading-relaxed">{c.messaggio || "—"}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
